import { Component, OnInit, ChangeDetectionStrategy, Input, OnDestroy, HostBinding, Optional, Self, forwardRef, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatFormFieldControl, MatDialog } from '@angular/material';
import { LookupModel } from '../../../common/models/LookupModel';
import { Subject, Subscription } from 'rxjs';
import { NgControl, ControlValueAccessor, AbstractControl } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';
import { DataWarehouseService } from '../../../core/services';
import { BehaviorSubject } from 'rxjs';
import { DataWarehouseEntityLookupModalComponent } from './data-warehouse-entity-lookup-modal.component';

@Component({
  selector: 'brd-data-warehouse-entity-lookup',
  templateUrl: './data-warehouse-entity-lookup.component.html',
  styleUrls: ['./data-warehouse-entity-lookup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MatFormFieldControl, useExisting: DataWarehouseEntityLookupComponent }
  ]
})
export class DataWarehouseEntityLookupComponent implements OnInit, OnDestroy, MatFormFieldControl<LookupModel>, ControlValueAccessor {
  static nextId = 0;

  _value: LookupModel;
  _placeholder: string;
  _required = false;
  _autoPopulate = false;
  _parentAutoPopulateValue: string;
  _disabled = false;
  _onModelChange = (v) => { };
  _onTouch = () => { };

  stateChanges = new Subject<void>();
  focused = false;
  controlType = 'brd-data-warehouse-entity-lookup';
  items = new BehaviorSubject<any[]>([]);
  totalCount = new BehaviorSubject<number>(0);
  loading = new BehaviorSubject<boolean>(false);

  _parentSubscription: Subscription;
  _parentDropdown: { entityKey: string, formName: string, displayName: string, control: AbstractControl, key: string };
  _relatedControl: { entityKey: string, formName: string, displayName: string, control: AbstractControl, key: string };
  hasParentAssigned : boolean= false;

  @Input()
  get value(): LookupModel {
    return this._value;
  }
  set value(value: LookupModel) {
    this._value = value;
    this.stateChanges.next();
    this._onModelChange(this._value);
    this.changeDetectorRef.markForCheck();
  }

  @HostBinding()
  id = `brd-data-warehouse-entity-lookup-${DataWarehouseEntityLookupComponent.nextId++}`;

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  @Input()
  get required() {
    return this._required;
  }
  set required(required) {
    this._required = coerceBooleanProperty(required);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get empty() {
    return !this.value || !this.value.key;
  }

  get errorState(): boolean {
    return this.ngControl ? this.ngControl.invalid : false;
  }

  @HostBinding('attr.aria-describedby') describedBy = '';

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  @Input()
  metadata: any;

  @Input()
  get parentDropdown(): { entityKey: string, formName: string, displayName: string }
  {
    return this._parentDropdown;
  }
  set parentDropdown(parent: { entityKey: string, formName: string, displayName: string }) {
    if (parent) {
      var parentControl = this.findControl(parent.formName);
      if (!parentControl) {
        throw new Error("Cannot find control with name: " + this._parentDropdown.formName);
      }
      this._parentDropdown = {
        ...parent,
        control: parentControl,
        key: parentControl.value ? parentControl.value.key : undefined
      };
      if (this._parentSubscription) {
        this._parentSubscription.unsubscribe();
      }
      this._parentSubscription = parentControl.valueChanges.subscribe(v => {
        if ((v && v.key !== this._parentDropdown.key) ||
          (!v && this._parentDropdown.key)) {
          this.writeValue(null);
          this._parentDropdown = {
            ...this._parentDropdown,
            key: v ? v.key : undefined
          };
          this.autoPopulateForOneOption();
        }
      });
    } else {
      if (this._parentSubscription) {
        this._parentSubscription.unsubscribe();
      }
    }
  }

  @Input()
  get relatedControl(): { entityKey: string, formName: string, displayName: string }
  {
    return this._relatedControl;
  }
  set relatedControl(ctrl: { entityKey: string, formName: string, displayName: string }) {
    if (ctrl) {
      var relatedCtrl = this.findControl(ctrl.formName);
      if (!relatedCtrl) {
        throw new Error("Cannot find control with name: " + this._relatedControl.formName);
      }
      this._relatedControl = {
        ...ctrl,
        control: relatedCtrl,
        key: relatedCtrl.value ? relatedCtrl.value : undefined
      };
    }
  }

  @Input()
  get autoPopulate() {
    return this._autoPopulate;
  }

  set autoPopulate(autoPopulate) {
    this._autoPopulate = coerceBooleanProperty(autoPopulate);
    this.stateChanges.next();
  }

  @Input()
  get parentAutoPopulateValue() {
    return this._parentAutoPopulateValue;
  }
  set parentAutoPopulateValue(val) {
    this._parentAutoPopulateValue = val;
    this.stateChanges.next();
  }

  constructor(@Optional() @Self() public ngControl: NgControl,
    private _focusMonitor: FocusMonitor,
    private _element: ElementRef,
    public dialog: MatDialog,
    private dataWarehouseService: DataWarehouseService,
    private changeDetectorRef: ChangeDetectorRef) {
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
    this._focusMonitor.monitor(this._element.nativeElement, true)
      .subscribe(o => {
        this.focused = !!o;
        this.stateChanges.next();
      });
  }

  ngOnInit() {
    this.loading.next(true);

    if(this._parentDropdown !== null && this._parentAutoPopulateValue !== null){
      this._parentDropdown.key = this._parentAutoPopulateValue;
      this.hasParentAssigned = true;
    }

    this.autoPopulateForOneOption();
  }

  autoPopulateForOneOption() {
    this.dataWarehouseService
      .getWarehouseList(
        this.metadata.classMetadataItems.entityKey,
        this._parentDropdown ? this._parentDropdown.entityKey : null,
        this._parentDropdown ? this._parentDropdown.key : null,
        0,
        20,
        undefined,
        "",
        "",
        this.dataWarehouseService.currentSubmittingEntityKey,
        false,
      )
      .take(1)
      .subscribe(r => {
        // TODO: handle errors
        if (!r.hasError) {
          this.loading.next(false);
          this.items.next(r.items);
          this.totalCount.next(r.totalCount);
          if (this._autoPopulate && this.items.value.length === 1
            && (this.value == null
              || (this.value != null
                && (this.value.key != this.items.value[0].key
                  || this.value.description != this.items.value[0].displayName)))) {
            this.value = {
                key: this.items.value[0].key,
                description: this.items.value[0].displayName
              }
            }
        }
      });
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._element.nativeElement);
    if (this._parentSubscription) {
      this._parentSubscription.unsubscribe();
    }
  }

  openSearch() {
    if (this._parentDropdown && this._parentAutoPopulateValue != null) {
      if (!this._parentDropdown.key)
        this._parentDropdown.key  = this._parentAutoPopulateValue;
      
    }
    if (!this._parentDropdown || this._parentDropdown.key) {
      let dialogRef = this.dialog.open(DataWarehouseEntityLookupModalComponent,
          {
            panelClass: 'customized-dialog',
            width: '80vw',
            data: { metadata: this.metadata, parent: this._parentDropdown }
          });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            this.value = {
              key: result.key,
              description: result.displayName
            }
            if(this.relatedControl){
              this._relatedControl.control.setValue(result.displayName);
            }
          }
        });
    }
  }

  onContainerClick(event: MouseEvent) {
    this._onTouch();
    this.openSearch();
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this._onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  private findControl(name: string): AbstractControl {
    var currentControl = this.ngControl.control.parent;
    var control : AbstractControl = undefined;
    do {
      control = currentControl.get(name);
      currentControl = currentControl.parent;
    } while ( !control && currentControl )
    return control;
  }

}



// WEBPACK FOOTER //
// ./src/app/datawarehouse/components/data-warehouse-entity-lookup/data-warehouse-entity-lookup.component.ts